<template>
	<div>
		<div v-for="room in items" :key="room.ID" class="room row contentblock">
			<div class="columns column6 col-image">
				<div v-if="room.images.length" :class="{ doublerow: room.images.length > 6 }">
					<a
						:data-src="room.images[0].image"
						:data-fancybox="`gallery-${room.ID}`"
						:data-caption="room.images[0].imageAlt"
						:title="room.images[0].imageAlt"
					>
						<picture>
							<source
								v-if="room.images[0].imageWebp"
								:srcset="room.images[0].imageWebp"
								type="image/webp"
							/>
							<source :srcset="room.images[0].image" />
							<img :src="room.images[0].image" :alt="room.images[0].imageAlt" loading="lazy" />
						</picture>
					</a>
				</div>
				<div v-if="room.images.slice(1).length" class="thumbnails">
					<a
						v-for="item in room.images.slice(1)"
						:key="item.image"
						:data-src="item.image"
						:data-fancybox="`gallery-${room.ID}`"
						:data-caption="item.imageAlt"
						:title="item.imageAlt"
					>
						<picture>
							<source v-if="item.imagesWebp" :srcset="item.imagesWebp[1]" type="image/webp" />
							<source :srcset="item.images[1]" />
							<img :src="item.images[1]" />
						</picture>
					</a>
				</div>
			</div>
			<div class="columns column6 col-content">
				<div class="contentblock-content">
					<h2>{{ room.header }}</h2>
					<div class="room-icon">
						<div v-if="room.pax">
							<font-awesome-icon :icon="`fas fa-users`" size="1x" />
							<span>{{ room.pax }}</span>
						</div>
						<div v-if="room.surface">
							<font-awesome-icon :icon="`fas fa-ruler-combined`" size="1x" />
							<span>{{ room.surface }}</span>
						</div>
					</div>
					<div v-parse-links v-html="room.content" />
					<book-button class="button cta">
						{{ $t('bookThisRoom') }}
					</book-button>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

defineProps({
	items: { type: Array, default: () => [] },
	filter: { type: String, default: 'showAll' },
});

onMounted(() => {
	Fancybox.bind('[data-fancybox]', {});
});
</script>

<style lang="scss" scoped>
.contentblock {
	display: flex;
	flex-wrap: wrap;
	align-items: stretch;
	justify-content: space-between;
	padding: 0;
	margin: 40px auto;
	position: relative;

	&:nth-child(odd) {
		flex-direction: row-reverse;

		.col-image {
			left: 0;
			right: auto;
		}
	}

	.columns:first-child {
		padding: 0;
	}

	img {
		object-fit: cover;
		width: 100%;
		height: 100%;
	}

	.button {
		border: solid 1px #231f20;
	}

	.button:hover {
		border: solid 1px #231f20;
	}

	.col-content {
		padding: 0;
		position: relative;
		z-index: 2;
		width: 45%;
		display: flex;
		flex-wrap: wrap;
		align-items: stretch;
	}

	.col-image {
		position: relative;
		width: 54%;
		display: flex;
		flex-flow: column wrap;
		justify-content: center;
		align-items: stretch;

		> div:first-child {
			height: 100%;
			padding-bottom: 80px;
		}

		a {
			cursor: pointer;
			display: inline-block;
			height: 100%;

			img {
				transition: opacity 0.3s ease;
			}

			&:hover img {
				opacity: 0.7;
			}
		}

		.thumbnails {
			bottom: 0;
			display: flex;
			gap: 10px;
			left: 0;
			margin: 0;
			position: absolute;
			right: 0;
			background: var(--primary-background-color);
			padding: 10px;

			img {
				max-width: 100px;
			}
		}
	}

	.contentblock-content {
		padding: 100px 75px;
		background: #fff;
	}
}

.room-icon {
	display: flex;
	flex-flow: row wrap;
	justify-content: flex-start;
	align-items: center;
	margin: 20px 0;

	div {
		margin: 5px 18px 5px 0;
	}

	svg {
		font-size: 16px;
		margin: 0 10px 0 0;
	}
}

@media (max-width: 1080px) {
	.contentblock {
		.col-image {
			width: 100%;
			position: relative;
		}

		.col-content {
			padding: 0;
			width: 100%;
			margin: 10px auto;
		}

		.contentblock-content {
			padding: 50px;
		}
	}
}

@media (max-width: 580px) {
	.contentblock {
		margin-bottom: 25px;

		.col-image {
			.doublerow {
				padding-bottom: 152px !important;
			}

			.thumbnails {
				flex-flow: row wrap;

				a {
					width: 100px;
					max-width: calc(20% - 8px);

					img {
						width: 100%;
					}
				}
			}
		}

		.contentblock-content {
			padding: 35px;
		}
	}
}

@media (max-width: 480px) {
	.contentblock {
		.col-image {
			div:first-child {
				padding-bottom: 60px;

				&.doublerow {
					padding-bottom: 110px !important;
				}
			}
		}
	}
}
</style>
